import { BffApiClientMolecule } from "@/app/api-client/store"
import { WalletConnectMoleculeReatom } from "@/app/wallet-connect/store.reatom"
import type { MagicEdenPoolOfferRef } from "@anaconda/core/bid/types"
import { getRawAddress } from "@anaconda/core/blockchain/address"
import { Blockchain } from "@anaconda/core/blockchain/types"
import type { Nft } from "@anaconda/core/nft/types"
import { Trans } from "@lingui/react/macro"
import {
  SolanaTokens,
  deserializeSolanaTransaction,
  fitSolanaTransactionToPacket,
  resolveMagicEdenTokenAccount,
  signAndSendSolanaTransaction,
  toFillAssetStandard,
} from "@rarible_int/anaconda_sdk/solana"
import { sonnerToast } from "@rarible_int/anaconda_ui_components"
import { action, withAsync, withCallHook, wrap } from "@reatom/core"
import { PublicKey } from "@solana/web3.js"
import { molecule } from "bunshi"
import { SolanaSdkContainerMoleculeReatom } from "../../blockchains/solana-sdk-container-molecule.reatom"
import { AppToast, SuccessViewTransaction } from "../../toasts/app-toast"
import { getTransactionErrorMessage } from "../events/transaction-event"
import { invalidateNftOffersQueries } from "../order/invalidate-nft-offers-queries"

export interface SolanaAcceptMagicEdenPoolBidParams {
  nft: Nft
  offer: MagicEdenPoolOfferRef
}

/**
 * Selling into a Magic Eden MMM pool — their AMM rather than their auction
 * house, so a different builder from
 * `SolanaAcceptMagicEdenOfferMoleculeReatom`. The shape is otherwise the same:
 * Magic Eden composes the transaction, the seller signs it, we send it.
 *
 * The price floor is deliberately not sent from here. A pool reprices along its
 * bonding curve as it fills, so the amount shown on the row is stale the moment
 * it renders; the BFF re-reads the pool and derives the floor itself.
 */
export const SolanaAcceptMagicEdenPoolBidMoleculeReatom = molecule((mol) => {
  const { sdkContainerAtom } = mol(SolanaSdkContainerMoleculeReatom)
  const bffApiClient = mol(BffApiClientMolecule)
  const { solanaAddress: solanaAddressAtom } = mol(WalletConnectMoleculeReatom)

  const acceptMagicEdenPoolBidActionFactory = () => {
    const acceptAction = action(
      async ({ nft, offer }: SolanaAcceptMagicEdenPoolBidParams) => {
        const from = solanaAddressAtom()
        const container = sdkContainerAtom()

        if (!from) {
          throw new Error(
            "Solana wallet address is not available to accept an offer",
          )
        }
        if (!container) {
          throw new Error("Solana container is not initialized")
        }
        if (!nft.nftStandard) {
          throw new Error("NFT standard is unknown for this item")
        }

        const connection = container.resolve(SolanaTokens.CONNECTION_ATOM)()
        const signer = container.resolve(SolanaTokens.SIGNER_ATOM)()

        // The seller must occupy a real signer slot in Magic Eden's message;
        // fail here rather than at the wallet with an opaque error.
        if (signer.publicKey?.toBase58() !== from) {
          throw new Error("Solana signer does not match the NFT owner")
        }

        const nftMint = getRawAddress(nft.id)
        // For MPL Core this is the asset address, for SPL/pNFT the owner's ATA
        // — matching what Magic Eden reports as `tokenAddress` on a listing.
        const assetTokenAccount = await wrap(
          resolveMagicEdenTokenAccount({
            connection,
            nftMint: new PublicKey(nftMint),
            owner: new PublicKey(from),
            nftStandard: toFillAssetStandard(nft.nftStandard),
          }),
        )

        const response = await wrap(
          bffApiClient.api.nfts["magic-eden"]["pool-sell"].$post({
            json: {
              collectionId: nft.collection.id,
              pool: offer.pool,
              seller: from,
              assetMint: nftMint,
              assetTokenAccount,
            },
          }),
        )

        if (response.status === 409) {
          throw new Error(
            "This Magic Eden offer is no longer available — the pool has been filled or withdrawn.",
          )
        }
        if (!response.ok) {
          throw new Error("Magic Eden could not create the accept transaction")
        }

        const payload = await wrap(response.json())
        // Prefer Magic Eden's own v0 message: re-encoding the legacy variant
        // strips the signer flag off their cosigner. `txSigned` is the fallback
        // for a response that carries no v0 at all.
        const data = payload.v0?.data ?? payload.txSigned?.data
        if (!data) {
          throw new Error("Magic Eden could not create the accept transaction")
        }

        const decoded = deserializeSolanaTransaction(Uint8Array.from(data))
        const transaction = await wrap(
          fitSolanaTransactionToPacket({
            connection,
            transaction: decoded,
            lookupTableAddresses: [],
          }),
        )

        const txId = await wrap(
          signAndSendSolanaTransaction({ connection, signer, transaction }),
        )
        return { txId }
      },
      "solanaAcceptMagicEdenPoolBidAction",
    ).extend(
      withAsync(),
      withCallHook(() => {
        sonnerToast.custom(
          (id) => (
            <AppToast.Pending
              id={id}
              title="Accept offer"
              main={<Trans>Accept offer</Trans>}
              second={<Trans>Pending...</Trans>}
            />
          ),
          { duration: Number.POSITIVE_INFINITY },
        )
      }),
    )

    acceptAction.onReject.extend(
      withCallHook(({ error }) => {
        const message = getTransactionErrorMessage(error)
        sonnerToast.dismiss()
        sonnerToast.custom((id) => (
          <AppToast.Error
            id={id}
            title="Accept offer"
            main={<Trans>Accepting the offer failed</Trans>}
            second={
              <div className="font-semibold text-destructive">{message}</div>
            }
          />
        ))
      }),
    )

    acceptAction.onFulfill.extend(
      withCallHook(({ payload, params: [params] }) => {
        invalidateNftOffersQueries(params.nft.id)
        sonnerToast.dismiss()
        sonnerToast.custom((id) => (
          <AppToast.Success
            id={id}
            title="Accept offer"
            main={<Trans>Offer accepted</Trans>}
            second={
              <SuccessViewTransaction
                transactionId={payload.txId}
                blockchain={Blockchain.SOLANA}
              />
            }
          />
        ))
      }),
    )

    return acceptAction
  }

  return { acceptMagicEdenPoolBidActionFactory }
})
